import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const FIELD_DEFINITIONS = [
  { id: 'vessel_name', label: 'Vessel', type: 'category' },
  { id: 'voyage_number', label: 'Voyage Number', type: 'category' },
  { id: 'port_name', label: 'Port/Position', type: 'category' },
  { id: 'ata', label: 'ATA', type: 'date' },
  { id: 'ats', label: 'ATS', type: 'date' },
  { id: 'actual_port_days', label: 'Actual Port Days', type: 'numeric' },
  { id: 'planned_days', label: 'Planned Port Days', type: 'numeric' },
  { id: 'laycan_deviation_days', label: 'Laycan Deviation Days', type: 'numeric' },
  { id: 'port_stay_deviation', label: 'Port Stay Deviation Days', type: 'numeric' },
  { id: 'laycan_status', label: 'Laycan Status', type: 'category' }
];

const AGGREGATIONS = [
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
  { id: 'count', label: 'Count' },
  { id: 'distinctCount', label: 'Distinct Count' },
  { id: 'min', label: 'Min' },
  { id: 'max', label: 'Max' },
  { id: 'median', label: 'Median' }
];

const DATE_GROUPINGS = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year', label: 'Year' }
];

const NUMBER_FORMATS = [
  { id: 'number', label: 'Number' },
  { id: 'currency', label: 'Currency' },
  { id: 'percent', label: 'Percent' },
  { id: 'integer', label: 'Integer' }
];

export default function PivotConfigDialog({ isOpen, onClose, onSave, editingWidget }) {
  const [title, setTitle] = useState('');
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([{ field: 'actual_port_days', aggregation: 'sum' }]);
  const [dateGrouping, setDateGrouping] = useState({});
  const [numberFormat, setNumberFormat] = useState('number');
  const [conditionalFormat, setConditionalFormat] = useState('none');
  const [showTotals, setShowTotals] = useState(true);
  const [draggedField, setDraggedField] = useState(null);

  useEffect(() => {
    if (editingWidget) {
      setTitle(editingWidget.title);
      setRows(editingWidget.config.rows || []);
      setColumns(editingWidget.config.columns || []);
      setValues(editingWidget.config.values || [{ field: 'actual_port_days', aggregation: 'sum' }]);
      setDateGrouping(editingWidget.config.dateGrouping || {});
      setNumberFormat(editingWidget.config.numberFormat || 'number');
      setConditionalFormat(editingWidget.config.conditionalFormat || 'none');
      setShowTotals(editingWidget.config.showTotals !== undefined ? editingWidget.config.showTotals : true);
    } else {
      setTitle('');
      setRows([]);
      setColumns([]);
      setValues([{ field: 'actual_port_days', aggregation: 'sum' }]);
      setDateGrouping({});
      setNumberFormat('number');
      setConditionalFormat('none');
      setShowTotals(true);
    }
  }, [editingWidget, isOpen]);

  const handleSave = () => {
    if (!title || rows.length === 0) {
      alert('Please provide a title and at least one row field');
      return;
    }

    const config = {
      rows,
      columns,
      values,
      dateGrouping,
      numberFormat,
      conditionalFormat,
      showTotals
    };

    onSave({
      type: 'advancedPivot',
      title,
      config
    });
  };

  const handleDrop = (target) => {
    if (!draggedField) return;
    
    // Remove from source
    if (draggedField.source === 'rows') {
      setRows(prev => prev.filter(f => f !== draggedField.field));
    } else if (draggedField.source === 'columns') {
      setColumns(prev => prev.filter(f => f !== draggedField.field));
    } else if (draggedField.source === 'values') {
      setValues(prev => prev.filter((v, i) => i !== draggedField.index));
    }
    
    // Add to target
    if (target === 'rows') {
      setRows(prev => [...prev, draggedField.field]);
    } else if (target === 'columns') {
      setColumns(prev => [...prev, draggedField.field]);
    } else if (target === 'values') {
      setValues(prev => [...prev, { field: draggedField.field, aggregation: 'sum' }]);
    }
    
    setDraggedField(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{editingWidget ? 'Edit Pivot Table' : 'Add Pivot Table'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Pivot Table Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Vessel Performance by Port"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Available Fields */}
            <div>
              <label className="text-sm font-medium block mb-2">Available Fields</label>
              <div className="space-y-1 max-h-60 overflow-y-auto border rounded p-2 bg-gray-50">
                {FIELD_DEFINITIONS.map(field => (
                  <div
                    key={field.id}
                    draggable
                    onDragStart={() => setDraggedField({ field: field.id, source: 'available' })}
                    className="px-2 py-1 bg-white border rounded cursor-move hover:bg-gray-100 text-sm"
                  >
                    {field.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Drop Zones */}
            <div className="space-y-4">
              {/* Rows */}
              <div>
                <label className="text-sm font-medium block mb-2">
                  Rows <span className="text-red-500">*</span>
                </label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop('rows')}
                  className="min-h-[80px] border-2 border-dashed rounded p-2 space-y-1 bg-blue-50"
                >
                  {rows.map((field, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-blue-100 px-2 py-1 rounded text-sm">
                      <span className="flex-1">
                        {FIELD_DEFINITIONS.find(f => f.id === field)?.label}
                      </span>
                      {FIELD_DEFINITIONS.find(f => f.id === field)?.type === 'date' && (
                        <select
                          value={dateGrouping[field] || 'day'}
                          onChange={(e) => setDateGrouping(prev => ({ ...prev, [field]: e.target.value }))}
                          className="text-xs border rounded px-1 py-0.5"
                        >
                          {DATE_GROUPINGS.map(g => (
                            <option key={g.id} value={g.id}>{g.label}</option>
                          ))}
                        </select>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRows(prev => prev.filter((_, i) => i !== idx))}
                        className="h-5 w-5 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {rows.length === 0 && (
                    <p className="text-xs text-gray-500 text-center py-2">Drag fields here</p>
                  )}
                </div>
              </div>

              {/* Columns */}
              <div>
                <label className="text-sm font-medium block mb-2">Columns (Optional)</label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop('columns')}
                  className="min-h-[80px] border-2 border-dashed rounded p-2 space-y-1 bg-green-50"
                >
                  {columns.map((field, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-green-100 px-2 py-1 rounded text-sm">
                      <span className="flex-1">
                        {FIELD_DEFINITIONS.find(f => f.id === field)?.label}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setColumns(prev => prev.filter((_, i) => i !== idx))}
                        className="h-5 w-5 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {columns.length === 0 && (
                    <p className="text-xs text-gray-500 text-center py-2">Drag fields here</p>
                  )}
                </div>
              </div>

              {/* Values */}
              <div>
                <label className="text-sm font-medium block mb-2">Values</label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop('values')}
                  className="min-h-[80px] border-2 border-dashed rounded p-2 space-y-1 bg-yellow-50"
                >
                  {values.map((value, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-yellow-100 px-2 py-1 rounded text-sm">
                      <span className="flex-1 text-xs">
                        {FIELD_DEFINITIONS.find(f => f.id === value.field)?.label}
                      </span>
                      <select
                        value={value.aggregation}
                        onChange={(e) => setValues(prev => 
                          prev.map((v, i) => i === idx ? { ...v, aggregation: e.target.value } : v)
                        )}
                        className="text-xs border rounded px-1 py-0.5"
                      >
                        {AGGREGATIONS.map(agg => (
                          <option key={agg.id} value={agg.id}>{agg.label}</option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setValues(prev => prev.filter((_, i) => i !== idx))}
                        className="h-5 w-5 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Formatting Options */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium block mb-2">Number Format</label>
              <select
                value={numberFormat}
                onChange={(e) => setNumberFormat(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                {NUMBER_FORMATS.map(fmt => (
                  <option key={fmt.id} value={fmt.id}>{fmt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Conditional Format</label>
              <select
                value={conditionalFormat}
                onChange={(e) => setConditionalFormat(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="none">None</option>
                <option value="heatmap">Heatmap</option>
                <option value="bars">Data Bars</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2 pt-7">
                <input
                  type="checkbox"
                  checked={showTotals}
                  onChange={(e) => setShowTotals(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">Show Totals</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} className="flex-1">
              {editingWidget ? 'Update Pivot Table' : 'Add Pivot Table'}
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
